import React, { useMemo, useState } from 'react';
import { ShieldAlert, Check, X, Pencil } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import type { PendingApproval } from './types';

interface Props {
  approval: PendingApproval;
  onDecide: (decision: 'approved' | 'rejected', notes?: string, overrideArgs?: Record<string, unknown>) => Promise<void>;
}

function safeStringify(v: unknown): string {
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

export const ToolApprovalCard: React.FC<Props> = ({ approval, onDecide }) => {
  const [busy, setBusy] = useState(false);
  const [showArgs, setShowArgs] = useState(false);
  const [editing, setEditing] = useState(false);
  const initial = useMemo(() => safeStringify(approval.toolArgs), [approval.toolArgs]);
  const [draft, setDraft] = useState<string>(initial);
  const [parseError, setParseError] = useState<string | null>(null);

  const handle = async (decision: 'approved' | 'rejected') => {
    let overrideArgs: Record<string, unknown> | undefined;
    if (decision === 'approved' && editing && draft.trim() && draft.trim() !== initial.trim()) {
      try {
        const parsed: unknown = JSON.parse(draft);
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
          setParseError('Gli argomenti devono essere un oggetto JSON');
          return;
        }
        overrideArgs = parsed as Record<string, unknown>;
        setParseError(null);
      } catch (err) {
        setParseError(`JSON non valido: ${(err as Error).message}`);
        return;
      }
    }
    setBusy(true);
    try { await onDecide(decision, undefined, overrideArgs); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-amber-300 bg-amber-50 rounded-lg p-3">
      <div className="flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-amber-900">
            Conferma necessaria: <code className="text-xs">{approval.toolName}</code>
          </div>
          <div className="text-xs text-amber-800 mt-1">
            L'assistente vuole eseguire questa operazione sensibile. Approva, rifiuta o modifica gli argomenti.
          </div>
          <div className="flex items-center gap-3 mt-1">
            <button
              type="button"
              className="text-[11px] text-amber-700 underline"
              onClick={() => setShowArgs(s => !s)}
            >
              {showArgs ? 'Nascondi' : 'Mostra'} argomenti
            </button>
            <button
              type="button"
              className="text-[11px] text-amber-700 underline inline-flex items-center gap-1"
              onClick={() => { setShowArgs(true); setEditing(e => !e); }}
            >
              <Pencil className="h-3 w-3" /> {editing ? 'Annulla modifica' : 'Modifica'}
            </button>
          </div>
          {showArgs && (
            editing ? (
              <textarea
                value={draft}
                onChange={(e) => { setDraft(e.target.value); setParseError(null); }}
                rows={Math.min(12, draft.split('\n').length + 1)}
                className="mt-2 w-full p-2 bg-card rounded border border-amber-300 text-[11px] font-mono focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500"
              />
            ) : (
              <pre className="mt-2 p-2 bg-card rounded border border-amber-200 text-[11px] overflow-x-auto">
                {initial}
              </pre>
            )
          )}
          {parseError && (
            <div className="mt-1 text-[11px] text-red-600">{parseError}</div>
          )}
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" disabled={busy} onClick={() => handle('approved')} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Check className="h-3 w-3 mr-1" /> Approva
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => handle('rejected')}>
              <X className="h-3 w-3 mr-1" /> Rifiuta
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
